import { useContext, useState } from "react";

import {
  Button,
  Modal,
  StepperStateContext,
  Typography,
} from "@king-ict/eupisi-ui/components";
import { Spinner } from "@king-ict/eupisi-ui/components/Spinner/Spinner";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { RequestStatusEnum } from "@king-ict/eupisi-ui/models";
import { useTypedSelector } from "../../../app/rootReducer";
import { useAppDispatch } from "../../../app/store";
import { useBusinessRule } from "../../../hooks/businessRule/useBusinessRule";
import { RequestStep } from "../../../models/Request/RequestStep";
import RequestStepType from "../../../models/Request/RequestStepType";
import { SaveResult } from "../../../models/Shared/SaveResult";
import { useSaveRequestMutation } from "../../../queries";
import {
  updateRequestErrors,
  updateRequest,
} from "../../../slices/requestSlice";
import { FinalConfirmModal } from "../modals";
import { useSaveRequestError } from "./useSaveRequestError";
import Request from "../../../models/Request/Request";

interface IProps {
  stepperState: any;
  stepperApi: any;
}

export const RequestDraftStepperActions = ({
  stepperState,
  stepperApi,
}: IProps) => {
  const stepperStateContext = useContext(StepperStateContext);

  const { isRequestDraft } = useBusinessRule();
  const { t } = useTranslation();

  const { requestId } = useParams<{ requestId: string }>();
  const request = useTypedSelector((state) => state.request.request);
  const dispatch = useAppDispatch();
  const { isApplicantParent } = useBusinessRule();

  const [saveRequestMutation] = useSaveRequestMutation();
  const { handleSaveRequestError, fileSizeTooLargeModal } =
    useSaveRequestError();
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [isSavingStep, setIsSavingStep] = useState(false);
  const [finalConfirmModalCode, setFinalConfirmModalCode] = useState<
    number | null
  >(null);

  const handleToggleConfirmModal = () => {
    setOpenConfirmModal((openConfirmModal) => !openConfirmModal);
  };

  const handleToggleFinalConfirmModal = () => {
    setFinalConfirmModalCode(null);
  };

  /*STEPPER*/
  const handleStepChange = async (stepType: RequestStepType) => {
    if (isSavingStep) return;

    if (stepType === RequestStepType.NextStep) {
      const isValid = stepperStateContext?.toNext?.();

      if (!isValid) return;
    } else if (stepType === RequestStepType.PreviousStep) {
      stepperStateContext?.toPrevious?.();
    }

    if (!request.id) return;

    setIsSavingStep(true);

    try {
      const result = await saveRequestMutation({
        item: request,
        stepType,
        useFetchForLargePayload:
          stepperState.currentStep === RequestStep.DocumentationData,
      }).unwrap();

      updateRequestStateAfterSave(result);
    } catch (error) {
      handleSaveRequestError(error);
    } finally {
      setIsSavingStep(false);
      window.scrollTo(0, 0);
    }
  };

  const handleNext = async () => {
    await handleStepChange(RequestStepType.NextStep);
  };

  const handlePrevious = async () => {
    await handleStepChange(RequestStepType.PreviousStep);
  };

  /*ACTION*/
  const handleCreateRequestFromDraft = async () => {
    try {
      const result = await saveRequestMutation({
        item: request,
        stepType: RequestStepType.NextStep,
      }).unwrap();
      if (result.isValid && result.result?.code) {
        handleToggleConfirmModal();
        setFinalConfirmModalCode(Number(result.result.code));
      } else if (result.validationFailures) {
        dispatch(updateRequestErrors(result.validationFailures));
      }
    } catch (error) {
      handleSaveRequestError(error);
    }
  };

  const updateRequestStateAfterSave = (result: SaveResult<Request>) => {
    if (result && result.isValid) {
      if (result?.result) {
        dispatch(updateRequest(result.result));
      }

      if (result.result?.subStep != null) {
        stepperApi.moveToSpecific(result.result?.step, result.result?.subStep);
      } else {
        stepperApi.moveToSpecific(result.result?.step);
      }
    } else {
      if (result.validationFailures) {
        dispatch(updateRequestErrors(result.validationFailures));
      }
    }
  };

  // only applicant has right for stepper actions
  if (!isApplicantParent()) return null;

  return (
    <>
      <Spinner show={isSavingStep} />

      <Row className="border-top border-2 bg-light p-3">
        <Col md={6} xs={6}>
          {stepperState.currentStep > RequestStep.ParentData ||
          (stepperState.currentStep === RequestStep.ParentData &&
            stepperState.currentSubstep > 0) ? (
            <Button
              onClick={handlePrevious}
              size="sm"
              color="primary"
              outline
              disabled={isSavingStep}
            >
              {t("request.prevStepButton")}
            </Button>
          ) : (
            <></>
          )}
        </Col>
        <Col className="text-right" md={6} xs={6}>
          {stepperState.currentStep < RequestStep.Approval &&
            isRequestDraft() && (
              <Button onClick={handleNext} size="sm" disabled={isSavingStep}>
                {t("request.nextStepButton")}
              </Button>
            )}
          {stepperState.currentStep === RequestStep.Approval &&
            isRequestDraft() && (
              <Button onClick={() => setOpenConfirmModal(true)} size="sm">
                {t("request.sendRequestButton")}
              </Button>
            )}
        </Col>
      </Row>

      <Modal
        type={"confirm"}
        title={t("request.sendRequestConfirmModalTitle")}
        open={openConfirmModal}
        confirmText={
          requestId
            ? t("request.editRequestButton")
            : t("request.sendRequestButton")
        }
        cancelText={t("common.cancel")}
        onClose={handleToggleConfirmModal}
        onConfirm={handleCreateRequestFromDraft}
      >
        <Typography component="div" variant="medium">
          {request.statusId === RequestStatusEnum.Draft
            ? t("request.sendRequestConfirmModalMessage", {
                institutionName: "",
                //request.selectedInstitution.name,
              })
            : t("request.sendRequestConfirmModalMessage")}
        </Typography>
      </Modal>

      {fileSizeTooLargeModal}

      <FinalConfirmModal
        request={request}
        isOpen={!!finalConfirmModalCode}
        onModalClose={handleToggleFinalConfirmModal}
        code={finalConfirmModalCode}
      />
    </>
  );
};
